// frontend/src/hooks/useEntitlement.js
import { useCallback, useEffect, useRef, useState } from "react";
import { supabase } from "../lib/supabaseClient"; // ✅ correct: hooks -> ../lib
import { isEmailSuperAdmin } from "../lib/superAdmin";

/**
 * Consolidated paywall note:
 * - App-level PaywallGuard is the PRIMARY gate for paid routes.
 * - This hook provides:
 *    - capability flags (reports/remediation)
 *    - plan identity (UI)
 *    - tier ("single" vs "top") for business entity limits
 *    - defensive checks during webhook lag
 *
 * Backwards compatibility:
 * - We KEEP isAuthenticated/isPaid so existing pages don't break.
 *
 * © 2026 Advanced Advisory Services, LLC. All Rights Reserved.
 */

// Keep strict: only statuses we treat as "paid entitlement"
const PAID_STATUSES = new Set(["active", "active_one_time"]);
const DEV_OVERRIDE_KEY = "aifine_dev_subscription_override_v1";

function safeInt(x, fallback = null) {
  const n = Number(x);
  if (!Number.isFinite(n)) return fallback;
  return Math.trunc(n);
}

function safeBool(x, fallback = false) {
  if (x === true) return true;
  if (x === false) return false;
  return fallback;
}

function uniqStrings(xs) {
  const out = [];
  const seen = new Set();
  (Array.isArray(xs) ? xs : []).forEach((v) => {
    const s = String(v || "").trim();
    if (!s) return;
    if (seen.has(s)) return;
    seen.add(s);
    out.push(s);
  });
  return out;
}

function safeParse(raw, fallback = null) {
  try {
    if (!raw) return fallback;
    return JSON.parse(raw);
  } catch {
    return fallback;
  }
}

function readDevOverrideIfAllowed(userEmail) {
  // Only Super Admin can use the override (prevents accidental impact)
  if (!isEmailSuperAdmin(userEmail)) return null;

  try {
    const raw = localStorage.getItem(DEV_OVERRIDE_KEY);
    const obj = safeParse(raw, null);
    if (!obj || obj.enabled !== true) return null;

    const mode = String(obj.mode || "none");
    if (mode === "free") return { mode: "free", plan_key: "" };
    if (mode === "plan") return { mode: "plan", plan_key: String(obj.plan_key || "") };
    return null;
  } catch {
    return null;
  }
}

/**
 * Decide "best" plan among multiple active subs.
 * We prefer the plan that grants the broadest capabilities.
 *
 * Ordering rules:
 *  1) higher max_businesses wins
 *  2) allow_consultant_workflow true wins
 *  3) allow_remediation true wins
 *  4) allow_reports true wins
 *  5) higher price_usd_cents wins
 *  6) most recent subscription wins (created_at)
 */
function chooseBestPlan(candidates) {
  const list = Array.isArray(candidates) ? candidates : [];
  if (!list.length) return null;

  const score = (p) => {
    const maxBiz = safeInt(p?.max_businesses, 0) || 0;
    const consultant = safeBool(p?.allow_consultant_workflow, false) ? 1 : 0;
    const remediation = safeBool(p?.allow_remediation, false) ? 1 : 0;
    const reports = safeBool(p?.allow_reports, false) ? 1 : 0;
    const price = safeInt(p?.price_usd_cents, 0) || 0;
    const t = Date.parse(p?.__sub_created_at || "") || 0;
    return [maxBiz, consultant, remediation, reports, price, t];
  };

  let best = list[0];
  let bestScore = score(best);

  for (let i = 1; i < list.length; i += 1) {
    const s = score(list[i]);
    let better = false;
    for (let k = 0; k < s.length; k += 1) {
      if (s[k] > bestScore[k]) {
        better = true;
        break;
      }
      if (s[k] < bestScore[k]) break;
    }
    if (better) {
      best = list[i];
      bestScore = s;
    }
  }

  return best;
}

function inferTierFromPlan(plan) {
  const maxBiz = safeInt(plan?.max_businesses, 1);
  if (maxBiz != null && maxBiz > 1) return "top";
  if (safeBool(plan?.allow_consultant_workflow, false)) return "top";
  if (String(plan?.plan_key || "").toLowerCase() === "global_pro") return "top";
  return "single";
}

export default function useEntitlement() {
  const mountedRef = useRef(true);

  const [loading, setLoading] = useState(true);

  // Back-compat fields
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isPaid, setIsPaid] = useState(false);

  // Identity + tier
  const [tier, setTier] = useState("single");
  const [rawPlanKey, setRawPlanKey] = useState(null);

  // Capability flags
  const [allowReports, setAllowReports] = useState(false);
  const [allowRemediation, setAllowRemediation] = useState(false);
  const [maxBusinesses, setMaxBusinesses] = useState(1);
  const [auditsPerPeriod, setAuditsPerPeriod] = useState(null);
  const [period, setPeriod] = useState(null);
  const [allowConsultantWorkflow, setAllowConsultantWorkflow] = useState(false);

  // Optional debug context
  const [paidStatus, setPaidStatus] = useState(null);
  const [userId, setUserId] = useState(null);

  const applyUnauthed = useCallback(() => {
    if (!mountedRef.current) return;

    setUserId(null);
    setIsAuthenticated(false);

    setIsPaid(false);
    setPaidStatus(null);

    setTier("single");
    setRawPlanKey(null);

    setAllowReports(false);
    setAllowRemediation(false);
    setMaxBusinesses(1);
    setAuditsPerPeriod(null);
    setPeriod(null);
    setAllowConsultantWorkflow(false);

    setLoading(false);
  }, []);

  const resolveEntitlement = useCallback(async () => {
    let session = null;

    try {
      const { data: sessData, error: sessErr } = await supabase.auth.getSession();
      if (sessErr) throw sessErr;
      session = sessData?.session || null;
    } catch {
      applyUnauthed();
      return;
    }

    const uid = session?.user?.id || null;
    const userEmail = session?.user?.email || "";

    if (!uid) {
      applyUnauthed();
      return;
    }

    if (!mountedRef.current) return;

    setUserId(uid);
    setIsAuthenticated(true);

    // ✅ Super Admin-only dev override
    const devOverride = readDevOverrideIfAllowed(userEmail);
    if (devOverride?.mode === "free") {
      if (!mountedRef.current) return;

      setIsPaid(false);
      setPaidStatus(null);

      setTier("single");
      setRawPlanKey(null);

      setAllowReports(false);
      setAllowRemediation(false);
      setMaxBusinesses(1);
      setAuditsPerPeriod(null);
      setPeriod(null);
      setAllowConsultantWorkflow(false);

      setLoading(false);
      return;
    }

    if (devOverride?.mode === "plan" && devOverride.plan_key) {
      // Fetch plan by key and apply as if paid
      try {
        const { data: plans, error: planErr } = await supabase
          .from("plans")
          .select(
            "plan_key, is_active, allow_reports, allow_remediation, max_businesses, audits_per_period, period, allow_consultant_workflow, price_usd_cents"
          )
          .eq("plan_key", devOverride.plan_key)
          .limit(1);

        if (planErr) throw planErr;

        const plan = Array.isArray(plans) ? plans[0] : null;
        if (!plan || safeBool(plan?.is_active, true) === false) {
          // Fall back to normal behavior if plan missing/inactive
        } else {
          if (!mountedRef.current) return;

          setIsPaid(true);
          setPaidStatus("dev_override");

          setRawPlanKey(plan.plan_key);

          const t = inferTierFromPlan(plan);
          setTier(t);

          setAllowReports(safeBool(plan.allow_reports, false));
          setAllowRemediation(safeBool(plan.allow_remediation, false));
          setMaxBusinesses(safeInt(plan.max_businesses, 1) ?? 1);
          setAuditsPerPeriod(plan.audits_per_period ?? null);
          setPeriod(plan.period ?? null);
          setAllowConsultantWorkflow(safeBool(plan.allow_consultant_workflow, false));

          setLoading(false);
          return;
        }
      } catch {
        // ignore and continue to normal resolution
      }
    }

    // Normal path: fetch active paid subscriptions
    try {
      const { data: subs, error: subsErr } = await supabase
        .from("subscriptions")
        .select("status, plan_key, created_at")
        .eq("user_id", uid)
        .in("status", Array.from(PAID_STATUSES))
        .order("created_at", { ascending: false });

      if (subsErr) throw subsErr;

      const subRows = Array.isArray(subs) ? subs : [];
      const paid = subRows.length > 0;

      if (!paid) {
        if (!mountedRef.current) return;

        setIsPaid(false);
        setPaidStatus(null);

        setTier("single");
        setRawPlanKey(null);

        setAllowReports(false);
        setAllowRemediation(false);
        setMaxBusinesses(1);
        setAuditsPerPeriod(null);
        setPeriod(null);
        setAllowConsultantWorkflow(false);

        setLoading(false);
        return;
      }

      if (!mountedRef.current) return;

      setIsPaid(true);
      setPaidStatus(subRows[0]?.status || "active");

      const planKeys = uniqStrings(subRows.map((r) => r?.plan_key));
      if (!planKeys.length) {
        setLoading(false);
        return;
      }

      const { data: plans, error: plansErr } = await supabase
        .from("plans")
        .select(
          "plan_key, is_active, allow_reports, allow_remediation, max_businesses, audits_per_period, period, allow_consultant_workflow, price_usd_cents"
        )
        .in("plan_key", planKeys);

      if (plansErr) throw plansErr;

      const planRows = Array.isArray(plans) ? plans : [];

      const candidates = planRows
        .map((p) => {
          const matchingSub = subRows.find((s) => String(s?.plan_key || "") === String(p?.plan_key || ""));
          return { ...p, __sub_created_at: matchingSub?.created_at || null, __sub_status: matchingSub?.status || null };
        })
        .filter((p) => safeBool(p?.is_active, true) !== false);

      const best = chooseBestPlan(candidates) || candidates[0] || null;

      if (!best) {
        setLoading(false);
        return;
      }

      setRawPlanKey(best.plan_key);

      const t = inferTierFromPlan(best);
      setTier(t);

      setAllowReports(safeBool(best.allow_reports, false));
      setAllowRemediation(safeBool(best.allow_remediation, false));
      setMaxBusinesses(safeInt(best.max_businesses, 1) ?? 1);
      setAuditsPerPeriod(best.audits_per_period ?? null);
      setPeriod(best.period ?? null);
      setAllowConsultantWorkflow(safeBool(best.allow_consultant_workflow, false));

      setLoading(false);
    } catch {
      // Fail closed
      applyUnauthed();
    }
  }, [applyUnauthed]);

  useEffect(() => {
    mountedRef.current = true;
    resolveEntitlement();
    return () => {
      mountedRef.current = false;
    };
  }, [resolveEntitlement]);

  useEffect(() => {
    const { data: sub } = supabase.auth.onAuthStateChange(() => {
      resolveEntitlement();
    });

    return () => {
      sub?.subscription?.unsubscribe?.();
    };
  }, [resolveEntitlement]);

  return {
    loading,

    // Back-compat
    isAuthenticated,
    isPaid,

    // New primary fields
    tier,
    isTopTier: tier === "top",
    planKey: rawPlanKey,

    allowReports,
    allowRemediation,
    maxBusinesses,
    auditsPerPeriod,
    period,
    allowConsultantWorkflow,

    // Debug
    paidStatus,
    userId,
  };
}